import { useEffect, useState } from "react";
import { FaToggleOn, FaToggleOff, FaChevronDown, FaChevronRight } from "react-icons/fa";
import magic from "./magic";
import { writeWaterParameter } from "./controllers/waterParameters";
import "./HotWaterPanel.css";

function HotWaterPanel({ visible = true }) {
	const [underfloor_pump, setUnderfloorPump] = useState(0);
	const [radiator_pump, setRadiatorPump] = useState(0);
	const [big_pump, setBigPump] = useState(0);
	const [fill_valve, setFillValve] = useState(0);
	const [flush_valve, setFlushValve] = useState(0);
	const [water_level, setWaterLevel] = useState(0);
	const [autoheat, setAutoheat] = useState(0);
	const [heatfloor, setHeatfloor] = useState(0);
	const [temp, setTemp] = useState(0);
	const [target_temp, setTargetTemp] = useState(0);
	const [autofill_level, setAutofillLevel] = useState(0);
	const [uptime, setUptime] = useState(0);

	const [immersion_heater_state, setImmersionHeaterState] = useState(0);
	const [immersion_timer_remaining, setImmersionTimerRemaining] = useState(0);

	const [battery_data_fresh, setBatteryDataFresh] = useState(0);
	const [soc_threshold, setSocThreshold] = useState(98);
	const [soc_timer_remaining, setSocTimerRemaining] = useState(0);
	const [watt_soc_threshold, setWattSocThreshold] = useState(90);
	const [watt_timer_remaining, setWattTimerRemaining] = useState(0);

	const [soc_timer_input, setSocTimerInput] = useState(24);
	const [watt_timer_input, setWattTimerInput] = useState(24);
	const [immersion_timer_input, setImmersionTimerInput] = useState(720);

	const [batteryExpanded, setBatteryExpanded] = useState(false);
	const [advancedExpanded, setAdvancedExpanded] = useState(false);

	useEffect(() => {
		const onHotWater = (data) => {
			setUnderfloorPump(data.underfloor_pump);
			setRadiatorPump(data.radiator_pump);
			setBigPump(data.big_pump);
			setFillValve(data.fill_valve);
			setFlushValve(data.flush_valve);
			setWaterLevel(data.water_level);
			setAutoheat(data.autoheat);
			setHeatfloor(data.heatfloor);
			setTemp(data.temp);
			setTargetTemp(data.target_temp);
			setAutofillLevel(data.autofill_level);
			if (data.uptime !== undefined) setUptime(data.uptime);
			if (data.immersion_heater_state !== undefined) setImmersionHeaterState(data.immersion_heater_state);
			if (data.immersion_timer_remaining !== undefined) setImmersionTimerRemaining(data.immersion_timer_remaining);
			if (data.battery_data_fresh !== undefined) setBatteryDataFresh(data.battery_data_fresh);
			if (data.soc_threshold !== undefined) setSocThreshold(data.soc_threshold);
			if (data.soc_timer_remaining !== undefined) setSocTimerRemaining(data.soc_timer_remaining);
			if (data.watt_soc_threshold !== undefined) setWattSocThreshold(data.watt_soc_threshold);
			if (data.watt_timer_remaining !== undefined) setWattTimerRemaining(data.watt_timer_remaining);
		};
		magic.bleEventDispatcher.on("hotWater", onHotWater);
		return () => {
			magic.bleEventDispatcher.removeListener("hotWater", onHotWater);
		};
	}, []);

    const write=(name,value)=>writeWaterParameter(name,value).catch(e=>magic.bleEventDispatcher.emit('command-error',e.message));
    const toggleAutoheat=()=>write('autoheat',autoheat?0:1);
    const toggleHeatfloor=()=>write('floor',heatfloor?0:1);
    const toggleFlushValve=()=>write('flush',flush_valve?0:1);
    const increaseTargetTemp=()=>write('target',Math.min(100,target_temp+1));
    const decreaseTargetTemp=()=>write('target',Math.max(0,target_temp-1));
    const increaseAutofillLevel=()=>write('autofill',Math.min(10,autofill_level+1));
    const decreaseAutofillLevel=()=>write('autofill',Math.max(0,autofill_level-1));
    const setImmersionTimer=minutes=>write('immersion',Math.max(0,Math.min(1440,minutes))*60);
    const setSocHeatingThreshold=value=>write('soc',Math.max(0,Math.min(100,value)));
    const setWattHeatingThreshold=value=>write('wattSoc',Math.max(0,Math.min(100,value)));
    const setSocHeatingTimer=hours=>write('socTimer',Math.max(0,Math.min(65535,hours))*3600);
    const setWattHeatingTimer=hours=>write('wattTimer',Math.max(0,Math.min(65535,hours))*3600);

	const formatUptime = (seconds) => {
		if (isNaN(seconds)) return "0s";
		const days = Math.floor(seconds / 86400);
		const hours = Math.floor((seconds % 86400) / 3600);
		const mins = Math.floor((seconds % 3600) / 60);
		let result = "";
		if (days > 0) result += `${days}d `;
		if (hours > 0) result += `${hours}h `;
		if (mins > 0 || result === "") result += `${mins}m`;
		return result.trim();
	};

	const formatTimer = (minutes) => {
		if (minutes <= 0) return "Off";
		const days = Math.floor(minutes / 1440);
		const hours = Math.floor((minutes % 1440) / 60);
		const mins = minutes % 60;
		let result = "";
		if (days > 0) result += `${days}d `;
		if (hours > 0) result += `${hours}h `;
		if (mins > 0 || result === "") result += `${mins}m`;
		return result.trim();
	};

	const formatHours = (hours) => {
		if (hours <= 0) return "Off";
		const days = Math.floor(hours / 24);
		const h = hours % 24;
		if (days > 0) return `${days}d ${h}h`;
		return `${h}h`;
	};

	const isWaterLevelLow = () => water_level < 4;

	const getImmersionStatus = () => {
		if (water_level < 4) return { text: "Low Water", color: "#c44" };
		if (immersion_timer_remaining <= 0) return { text: "Off", color: "#666" };
		if (immersion_heater_state) return { text: "Heating", color: "#3a6" };
		return { text: "Standby", color: "#fa0" };
	};

	const immersionStatus = getImmersionStatus();

	if (!visible) return null;

	return (
		<div className="hotwater-panel">
			{/* Temperature Status Card */}
			<div className="hotwater-status-card">
				<div className="hotwater-status-left">
					<div className="hotwater-temp-display">
						<span className="hotwater-temp-value">{temp}</span>
						<span className="hotwater-temp-unit">°C</span>
					</div>
					<div className="hotwater-status-label">Water Temperature</div>
				</div>
				<div className="hotwater-target-control">
					<span className="hotwater-target-label">Target</span>
					<div className="hotwater-stepper compact">
						<button className="hotwater-stepper-btn" onClick={decreaseTargetTemp}>−</button>
						<span className="hotwater-stepper-value">{target_temp}°C</span>
						<button className="hotwater-stepper-btn" onClick={increaseTargetTemp}>+</button>
					</div>
				</div>
			</div>

			{/* Water Level */}
			<div className="hotwater-card">
				<div className="hotwater-card-header">Water Level</div>
				<div className="hotwater-level-display">
					<div className="hotwater-level-bar">
						<div
							className="hotwater-level-fill"
							style={{ height: `${water_level * 10}%` }}
						></div>
					</div>
					<div className="hotwater-level-info">
						<span className="hotwater-level-value">{water_level * 10}%</span>
						{isWaterLevelLow() && (
							<span className="hotwater-level-warning">⚠️ Low</span>
						)}
					</div>
				</div>
				<div className="hotwater-autofill">
					<span className="hotwater-autofill-label">Autofill to</span>
					<div className="hotwater-stepper compact">
						<button className="hotwater-stepper-btn small" onClick={decreaseAutofillLevel}>−</button>
						<span className="hotwater-stepper-value small">{autofill_level * 10}%</span>
						<button className="hotwater-stepper-btn small" onClick={increaseAutofillLevel}>+</button>
					</div>
				</div>
			</div>

			{/* Quick Controls Grid */}
			<div className="hotwater-controls-grid">
				<button
					className={`hotwater-control-tile ${autoheat ? 'active' : ''}`}
					onClick={toggleAutoheat}
				>
					<span className="hotwater-tile-icon">🔥</span>
					<span className="hotwater-tile-label">Diesel Heat</span>
					<span className="hotwater-tile-status">{autoheat ? 'On' : 'Off'}</span>
				</button>

				<button
					className={`hotwater-control-tile ${heatfloor ? 'active' : ''}`}
					onClick={toggleHeatfloor}
				>
					<span className="hotwater-tile-icon">🏠</span>
					<span className="hotwater-tile-label">Underfloor</span>
					<span className="hotwater-tile-status">{heatfloor ? 'On' : 'Off'}</span>
				</button>
			</div>

			{/* Immersion Heater */}
			<div className="hotwater-card">
				<div className="hotwater-card-row">
					<div>
						<div className="hotwater-card-header" style={{ marginBottom: 4 }}>Immersion Heater</div>
						<div className="hotwater-immersion-status" style={{ color: immersionStatus.color }}>
							{immersionStatus.text}
						</div>
					</div>
					<div className="hotwater-immersion-buttons">
						<button
							className="hotwater-btn start"
							onClick={() => setImmersionTimer(immersion_timer_input)}
							disabled={isWaterLevelLow()}
						>
							Start
						</button>
						<button
							className="hotwater-btn stop"
							onClick={() => setImmersionTimer(0)}
						>
							Stop
						</button>
					</div>
				</div>
				{immersion_timer_remaining > 0 && (
					<div className="hotwater-immersion-timer">
						<span className="hotwater-timer-label">Time remaining:</span>
						<span className="hotwater-timer-value">{formatTimer(immersion_timer_remaining)}</span>
					</div>
				)}
				<div className="hotwater-timer-input">
					<span className="hotwater-timer-input-label">Duration:</span>
					<input
						type="number"
						min="0"
						max="1440"
						value={immersion_timer_input}
						onChange={(e) => setImmersionTimerInput(Math.max(0, Math.min(1440, parseInt(e.target.value) || 0)))}
						className="hotwater-input"
					/>
					<span className="hotwater-timer-input-unit">min</span>
				</div>
			</div>

			{/* Battery Heating Accordion */}
			<div className="hotwater-card">
				<button
					className="hotwater-accordion-header"
					onClick={() => setBatteryExpanded(!batteryExpanded)}
				>
					<span>🔋 Battery Heating</span>
					{batteryExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
				</button>

				{batteryExpanded && (
					<div className="hotwater-accordion-content">
						{!battery_data_fresh && (
							<div className="hotwater-warning-banner">
								⚠️ Battery data is stale
							</div>
						)}

						{/* SOC-Based Heating */}
						<div className="hotwater-subsection">
							<div className="hotwater-subsection-header">
								<span>SOC-Based Heating</span>
								<span className={`hotwater-feature-status ${soc_timer_remaining > 0 ? 'enabled' : ''}`}>
									{soc_timer_remaining > 0 ? 'Enabled' : 'Disabled'}
								</span>
							</div>
							{soc_timer_remaining > 0 ? (
								<>
									<div className="hotwater-feature-row">
										<span>SOC Threshold</span>
										<div className="hotwater-stepper compact">
											<button className="hotwater-stepper-btn small" onClick={() => setSocHeatingThreshold(soc_threshold - 1)}>−</button>
											<span className="hotwater-stepper-value small">{soc_threshold}%</span>
											<button className="hotwater-stepper-btn small" onClick={() => setSocHeatingThreshold(soc_threshold + 1)}>+</button>
										</div>
									</div>
									<div className="hotwater-feature-row">
										<span>Time Remaining</span>
										<span className="hotwater-feature-value">{formatHours(soc_timer_remaining)}</span>
									</div>
									<button className="hotwater-btn stop full" onClick={() => setSocHeatingTimer(0)}>Disable</button>
								</>
							) : (
								<div className="hotwater-enable-row">
									<input
										type="number"
										min="0"
										max="65535"
										value={soc_timer_input}
										onChange={(e) => setSocTimerInput(Math.max(0, parseInt(e.target.value) || 0))}
										className="hotwater-input"
									/>
									<span className="hotwater-input-unit">hours</span>
									<button className="hotwater-btn start" onClick={() => setSocHeatingTimer(soc_timer_input)}>Enable</button>
								</div>
							)}
						</div>

						{/* Wattage-Based Heating */}
						<div className="hotwater-subsection">
							<div className="hotwater-subsection-header">
								<span>Wattage-Based Heating</span>
								<span className={`hotwater-feature-status ${watt_timer_remaining > 0 ? 'enabled' : ''}`}>
									{watt_timer_remaining > 0 ? 'Enabled' : 'Disabled'}
								</span>
							</div>
							{watt_timer_remaining > 0 ? (
								<>
									<div className="hotwater-feature-row">
										<span>Power Threshold</span>
										<span className="hotwater-feature-value">110W</span>
									</div>
									<div className="hotwater-feature-row">
										<span>Min SOC</span>
										<div className="hotwater-stepper compact">
											<button className="hotwater-stepper-btn small" onClick={() => setWattHeatingThreshold(watt_soc_threshold - 1)}>−</button>
											<span className="hotwater-stepper-value small">{watt_soc_threshold}%</span>
											<button className="hotwater-stepper-btn small" onClick={() => setWattHeatingThreshold(watt_soc_threshold + 1)}>+</button>
										</div>
									</div>
									<div className="hotwater-feature-row">
										<span>Time Remaining</span>
										<span className="hotwater-feature-value">{formatHours(watt_timer_remaining)}</span>
									</div>
									<button className="hotwater-btn stop full" onClick={() => setWattHeatingTimer(0)}>Disable</button>
								</>
							) : (
								<div className="hotwater-enable-row">
									<input
										type="number"
										min="0"
										max="65535"
										value={watt_timer_input}
										onChange={(e) => setWattTimerInput(Math.max(0, parseInt(e.target.value) || 0))}
										className="hotwater-input"
									/>
									<span className="hotwater-input-unit">hours</span>
									<button className="hotwater-btn start" onClick={() => setWattHeatingTimer(watt_timer_input)}>Enable</button>
								</div>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Advanced Settings Accordion */}
			<div className="hotwater-card">
				<button
					className="hotwater-accordion-header"
					onClick={() => setAdvancedExpanded(!advancedExpanded)}
				>
					<span>⚙️ Advanced</span>
					{advancedExpanded ? <FaChevronDown size={14} /> : <FaChevronRight size={14} />}
				</button>

				{advancedExpanded && (
					<div className="hotwater-accordion-content">
						<div className="hotwater-stats-grid">
							<div className="hotwater-stat-item">
								<span className="hotwater-stat-label">Fill Valve</span>
								<span className="hotwater-stat-value">{fill_valve ? <FaToggleOn color="#3a6" /> : <FaToggleOff color="#666" />}</span>
							</div>
							<div className="hotwater-stat-item clickable" onClick={toggleFlushValve}>
								<span className="hotwater-stat-label">Flush Valve</span>
								<span className="hotwater-stat-value">{flush_valve ? <FaToggleOn color="#3a6" /> : <FaToggleOff color="#666" />}</span>
							</div>
							<div className="hotwater-stat-item">
								<span className="hotwater-stat-label">Engine Pump</span>
								<span className="hotwater-stat-value">{big_pump ? <FaToggleOn color="#3a6" /> : <FaToggleOff color="#666" />}</span>
							</div>
							<div className="hotwater-stat-item">
								<span className="hotwater-stat-label">Radiator Pump</span>
								<span className="hotwater-stat-value">{radiator_pump ? <FaToggleOn color="#3a6" /> : <FaToggleOff color="#666" />}</span>
							</div>
							<div className="hotwater-stat-item">
								<span className="hotwater-stat-label">Underfloor Pump</span>
								<span className="hotwater-stat-value">{underfloor_pump ? <FaToggleOn color="#3a6" /> : <FaToggleOff color="#666" />}</span>
							</div>
							<div className="hotwater-stat-item">
								<span className="hotwater-stat-label">Uptime</span>
								<span className="hotwater-stat-value">{formatUptime(uptime)}</span>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}


export default HotWaterPanel;
